import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Palette, Check, Circle, Square, RectangleHorizontal, Sparkles, Waves } from 'lucide-react';
import { useTheme, themeColors, buttonShapes, bgEffects } from '../context/ThemeContext';

const ThemeSidePanel = () => {
  const {
    currentTheme, changeTheme, themeColor,
    buttonShape, changeButtonShape, currentButtonShape,
    bgEffect, changeBgEffect,
    isSidePanelOpen, setIsSidePanelOpen
  } = useTheme();

  const shapeIcons = { pill: RectangleHorizontal, rounded: Square, sharp: Square };

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsSidePanelOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-50 p-3 rounded-l-xl backdrop-blur-md transition-all hover:pr-5 border border-r-0"
        style={{
          backgroundColor: 'rgba(10, 10, 10, 0.4)',
          borderColor: `rgba(${themeColor.rgb}, 0.3)`
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ x: 100 }}
        animate={{ x: 0 }}
        transition={{ delay: 2.5 }}
        data-testid="theme-panel-toggle"
      >
        <Palette style={{ color: themeColor.primary, opacity: 0.8 }} size={22} />
      </motion.button>

      <AnimatePresence>
        {isSidePanelOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidePanelOpen(false)}
            />

            <motion.div
              className="fixed right-0 top-0 h-full w-80 backdrop-blur-xl border-l z-[61] p-6 overflow-y-auto"
              style={{
                backgroundColor: 'rgba(10, 10, 10, 0.7)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-bold text-white/90">Tema Ayarları</h3>
                  <p className="text-sm text-zinc-500">Siteyi kişiselleştir</p>
                </div>
                <button
                  onClick={() => setIsSidePanelOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors text-zinc-400 hover:text-white"
                  data-testid="theme-panel-close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* ── Accent Color ── */}
              <div className="space-y-4 mb-8">
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Accent Rengi</p>
                <div className="grid grid-cols-4 gap-2.5">
                  {themeColors.map((color) => (
                    <motion.button
                      key={color.value}
                      onClick={() => changeTheme(color.value)}
                      className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border backdrop-blur-sm transition-all ${
                        currentTheme === color.value
                          ? 'border-white/30 bg-white/10'
                          : 'border-white/10 hover:border-white/20 bg-white/5'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      data-testid={`theme-color-${color.value}`}
                    >
                      <div
                        className="w-7 h-7 rounded-full"
                        style={{ backgroundColor: color.primary }}
                      />
                      <span className="text-[10px] text-white/60">{color.name}</span>
                      {currentTheme === color.value && (
                        <motion.div
                          className="absolute -top-1 -right-1"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        >
                          <Check size={12} className="p-0.5 rounded-full bg-white text-black" />
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* ── Button Shape ── */}
              <div className="space-y-4 mb-8">
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Buton Şekli</p>
                <div className="grid grid-cols-3 gap-3">
                  {buttonShapes.map((shape) => (
                    <motion.button
                      key={shape.value}
                      onClick={() => changeButtonShape(shape.value)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-xl border backdrop-blur-sm transition-all ${
                        buttonShape === shape.value
                          ? 'border-white/30 bg-white/10'
                          : 'border-white/10 hover:border-white/20 bg-white/5'
                      }`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      data-testid={`theme-shape-${shape.value}`}
                    >
                      <div
                        className="w-16 h-6 border-2 transition-all"
                        style={{
                          borderColor: buttonShape === shape.value ? themeColor.primary : 'rgba(255,255,255,0.2)',
                          borderRadius: shape.radius,
                        }}
                      />
                      <span className="text-[10px] text-white/60">{shape.name}</span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* ── Background Effect ── */}
              <div className="space-y-4 mb-8">
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Arkaplan Efekti</p>
                <div className="grid grid-cols-2 gap-3">
                  {bgEffects.map((effect) => {
                    const icons = {
                      particles: Sparkles,
                      stars: Circle,
                      gradient: Waves,
                      none: X,
                    };
                    const Icon = icons[effect.value] || Circle;
                    return (
                      <motion.button
                        key={effect.value}
                        onClick={() => changeBgEffect(effect.value)}
                        className={`flex items-center gap-2.5 p-3.5 rounded-xl border backdrop-blur-sm transition-all ${
                          bgEffect === effect.value
                            ? 'border-white/30 bg-white/10'
                            : 'border-white/10 hover:border-white/20 bg-white/5'
                        }`}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        data-testid={`theme-bg-${effect.value}`}
                      >
                        <Icon size={16} style={{ color: bgEffect === effect.value ? themeColor.primary : 'rgba(255,255,255,0.4)' }} />
                        <span className="text-xs text-white/70">{effect.name}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* ── Preview ── */}
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-4">Önizleme</p>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xl" style={{ color: themeColor.primary }}>✶</span>
                    <div>
                      <div className="text-white/90 font-medium">canervac</div>
                      <div className="text-xs text-zinc-500">Developer</div>
                    </div>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="h-2 rounded-full bg-white/10" />
                    <div className="h-2 rounded-full bg-white/10 w-3/4" />
                    <div className="h-2 rounded-full w-1/2" style={{ backgroundColor: themeColor.primary }} />
                  </div>
                  <button
                    className="w-full py-2.5 text-sm font-medium text-black transition-all"
                    style={{
                      backgroundColor: themeColor.primary,
                      borderRadius: currentButtonShape.radius,
                    }}
                    data-testid="theme-preview-btn"
                  >
                    Örnek Buton
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-zinc-400">
                  Seçtiğiniz tema ayarları tarayıcınıza kaydedilir ve sonraki ziyaretlerinizde hatırlanır.
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ThemeSidePanel;
